import type { CaptureResult } from "posthog-js";
import { sanitizePath, sanitizeUrl } from "./sanitize-url";

// rrweb's Meta event, which stores the page URL shown in the replay player and its URL list.
const RRWEB_META_EVENT = 4;

// Heatmap points are keyed by the raw location.href, so they skip the $current_url cleanup.
function sanitizeHeatmapData(heatmapData: Record<string, unknown[]>): Record<string, unknown[]> {
  const sanitized: Record<string, unknown[]> = {};
  for (const [url, points] of Object.entries(heatmapData)) {
    const key = sanitizeUrl(url);
    sanitized[key] = [...(sanitized[key] ?? []), ...points];
  }
  return sanitized;
}

function sanitizeSnapshotData(snapshotData: unknown[]): unknown[] {
  return snapshotData.map((snapshot) => {
    if (typeof snapshot !== "object" || snapshot === null) return snapshot;
    const { type, data } = snapshot as { type?: unknown; data?: { href?: unknown } };
    if (type !== RRWEB_META_EVENT || typeof data?.href !== "string") return snapshot;
    return { ...snapshot, data: { ...data, href: sanitizeUrl(data.href) } };
  });
}

export function sanitizeEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  const { properties } = event;
  if (typeof properties.$current_url === "string") {
    properties.$current_url = sanitizeUrl(properties.$current_url);
  }
  if (typeof properties.$pathname === "string") {
    properties.$pathname = sanitizePath(properties.$pathname);
  }
  if (typeof properties.$referrer === "string") {
    properties.$referrer = sanitizeUrl(properties.$referrer);
  }
  if (typeof properties.$heatmap_data === "object" && properties.$heatmap_data !== null) {
    properties.$heatmap_data = sanitizeHeatmapData(properties.$heatmap_data);
  }
  if (Array.isArray(properties.$snapshot_data)) {
    properties.$snapshot_data = sanitizeSnapshotData(properties.$snapshot_data);
  }
  return event;
}
